import React from 'react';
import { useAuth } from 'react-oidc-context';
import { Switch } from '@progress/kendo-react-inputs';

import { DropDownList } from '@progress/kendo-react-dropdowns';
import {
  NotificationName,
  NotificationTypeResponse
} from '../../types/responses/notification-type-response';
import HelpSupport from '../../components/help-support';
import { UserResponse } from '../../types/responses/user-response';

export const notificationGroupMap = {
  Event: 'Events',
  Game: 'Game Templates',
  Assessment: 'Assessment Templates',
  Feedback: 'Feedback Templates',
  GlobalLibrary: 'Global Library'
};

export type groupedType = Record<
  keyof typeof notificationGroupMap,
  (NotificationTypeResponse & {
    checkedApp?: boolean;
    checkedEmail?: boolean;
  })[]
>;

interface UserSettingsProps {
  handleChange: (
    type: 'app' | 'email',
    notificationName: NotificationName,
    checked: boolean
  ) => void;
  isLoading: boolean;
  notificationTypes: groupedType[];
  userSettings: UserResponse;
  onAllowsEmailMeChange: (checked: boolean) => void;
  onEmailFrequencyChange: (frequency: string) => void;
}

const UserSettings: React.FC<UserSettingsProps> = ({
  isLoading,
  notificationTypes,
  userSettings,
  handleChange,
  onAllowsEmailMeChange,
  onEmailFrequencyChange
}) => {
  const auth = useAuth();

  return (
    <div className={'flex-grow-1 pt-2 d-flex flex-column gap-2'}>
      <div
        className={
          'border-2 border-bottom d-flex align-items-center justify-content-between pb-2'
        }>
        <div className={'text-success d-flex align-items-center pb-2 gap-2'}>
          <span className={'material-symbols-outlined'}>settings</span>
          <h2 className={'m-0'}>Settings</h2>
        </div>
        <HelpSupport
          title="Adjusting notification settings"
          url="https://forum.catalystglobal.com/t/my-profile-settings"
        />
      </div>

      <div
        className={'mt-2 border-2 border-bottom pb-3 d-flex flex-column gap-3'}>
        <div className="row">
          <div className="col-12 row">
            <div className="col-12">
              <Switch
                checked={userSettings.allowsEmailMe}
                onChange={(e) => onAllowsEmailMeChange(e.target.value)}
              />
              <span className="ml-3">
                {"Sent a notification digest email if I don't visit the app"}
              </span>
            </div>
          </div>
        </div>

        <div className="col-12">
          Select which notifications you want to receive.{' '}
          <b>In-App notifications</b> will appear in your Notifications tab.{' '}
          <b>Email notifications</b> are sent as a digest based on your chosen
          frequency.
        </div>
        <div>
          <span className="mr-3">Set your preferred digest frequency:</span>
          <DropDownList
            style={{
              backgroundColor: 'white',
              width: '150px'
            }}
            value={userSettings.emailFrequency}
            data={['Daily', 'Weekly', 'Monthly']}
            popupSettings={{
              animate: false
            }}
            onChange={(e) => onEmailFrequencyChange(e.target.value)}
          />
        </div>
      </div>

      <div className={'border-2 border-bottom'}>
        <div className="row mb-2">
          <div className="col-9">
            <div className="col-6 d-flex align-items-center justify-content-left">
              <span>Activity type</span>
            </div>
          </div>
          <div className="col-3 row">
            <div className="col-6 d-flex align-items-center justify-content-center gap-1">
              <span className="material-symbols-outlined">mobile</span>
              <span>In-App</span>
            </div>
            <div className="col-6 d-flex align-items-center justify-content-center gap-1">
              <span className="material-symbols-outlined">mail</span>
              <span>Email</span>
            </div>
          </div>
        </div>
      </div>

      <div className={'mt-2 flex-grow-1'}>
        {isLoading ? (
          <div className="d-flex flex-grow-1 h-100 justify-content-center align-items-center">
            Loading ...
          </div>
        ) : (
          <>
            {notificationTypes.map((notification, idx) => {
              const [title] = Object.keys(notification) as [
                keyof typeof notificationGroupMap
              ];
              const [group] = Object.values(notification);

              return (
                <React.Fragment key={idx}>
                  <span className="text-black-50" style={{ fontSize: '14px' }}>
                    {notificationGroupMap[title]}
                  </span>

                  {group.map((row) => (
                    <div className="row" key={row.id}>
                      <div className="col-9">
                        <p>{row.displayName}</p>
                      </div>
                      <div className="col-3 row">
                        <div className="col-6 text-center">
                          <Switch
                            defaultChecked={row.checkedApp}
                            onChange={(e) => {
                              handleChange('app', row.name, e.target.value);
                            }}
                          />
                        </div>
                        <div className="col-6 text-center ">
                          <Switch
                            defaultChecked={row.checkedEmail}
                            onChange={(e) => {
                              handleChange('email', row.name, e.target.value);
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </React.Fragment>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
};

export default UserSettings;
